import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import { ACTIONS, logAction } from '../../services/actionLog'
import './PurchaseAgreementPanel.css'
import { toast } from '../Toast'

/**
 * The details the Vehicle Purchase Agreement prints and nothing else.
 *
 * These twenty-odd fields were added to the database before there was anywhere to type
 * them, so every agreement rendered "Not recorded" down most of its length. This is that
 * screen. It is deliberately separate from Edit Deal: the order here follows the order of
 * the printed agreement, because the person filling it in usually has the form in front of
 * them.
 *
 * Two things worth knowing before changing this file:
 *
 *  - Colour and body are vehicle_color and vehicle_body. There are no vehicle_colour
 *    or vehicle_body_type columns any more; they were added by mistake and dropped in
 *    migration 036.
 *  - The bank fields are readable by every authenticated user, because RLS on deals is
 *    row-level and cannot hide three columns. That is recorded in migration 035 and is the
 *    reason the panel says so on screen rather than quietly collecting them.
 */

interface Props {
  deal: Deal
  user: User
  onSaved: (message: string) => void
}

const INCLUSIONS: Array<[string, string]> = [
  ['service_books', 'Service books / full service history'],
  ['owners_manual', "Owner's manual"],
  ['two_keys', '2 x keys'],
  ['one_key', '1 x key only'],
  ['rwc', 'RWC / safety certificate'],
]

const WRITTEN_OFF: Array<[string, string]> = [
  ['', 'Not recorded'],
  ['never_written_off', 'Never written off'],
  ['statutory', 'Statutory write-off'],
  ['repairable', 'Repairable write-off'],
]

const LCT: Array<[string, string]> = [
  ['', 'Not recorded'],
  ['not_applicable', 'Not applicable'],
  ['previously_paid', 'Previously paid - no liability remaining'],
]

const STATES = ['', 'VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT']

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

/** '' means "nobody filled this in", which must reach the database as null, not as 0. */
const numOrNull = (v: string) => {
  const t = v.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

const textOrNull = (v: string) => {
  const t = v.trim()
  return t ? t : null
}

export default function PurchaseAgreementPanel({ deal, user, onSaved }: Props) {
  const [form, setForm] = useState({
    vehicle_color: str(deal.vehicle_color),
    vehicle_body: str(deal.vehicle_body),
    engine_number: str(deal.engine_number),
    registration_state: str(deal.registration_state),
    written_off_status: str(deal.written_off_status),

    seller_legal_name: str(deal.seller_legal_name),
    seller_lmct: str(deal.seller_lmct),
    seller_suburb: str(deal.seller_suburb),
    seller_state: str(deal.seller_state),
    seller_postcode: str(deal.seller_postcode),

    seller_bank_name: str(deal.seller_bank_name),
    seller_bsb: str(deal.seller_bsb),
    seller_account_number: str(deal.seller_account_number),
    seller_bank_account_name: str(deal.seller_bank_account_name),

    deposit_amount: str(deal.deposit_amount),
    balance_payable: str(deal.balance_payable),
    expected_settlement_date: str(deal.expected_settlement_date),
    lct_status: str(deal.lct_status),

    financier_name: str(deal.financier_name),
    ppsr_finance_amount: str(deal.ppsr_finance_amount),
    payout_reference: str(deal.payout_reference),
    payout_letter_date: str(deal.payout_letter_date),

    known_defects: str(deal.known_defects),
  })

  const [inclusions, setInclusions] = useState<string[]>(
    Array.isArray(deal.inclusions) ? deal.inclusions : []
  )
  /* null and [] are different answers: null is "nobody was asked", [] is "asked, none of
     them". The agreement prints them differently, so the panel has to keep them apart. */
  const [inclusionsAsked, setInclusionsAsked] = useState(Array.isArray(deal.inclusions))

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const toggle = (key: string) => {
    setInclusionsAsked(true)
    setInclusions((cur) => (cur.indexOf(key) === -1 ? cur.concat(key) : cur.filter((c) => c !== key)))
  }

  const price = deal.valuation_amount != null ? Number(deal.valuation_amount) : null
  const deposit = numOrNull(form.deposit_amount)
  const balance = numOrNull(form.balance_payable)
  const impliedBalance = price != null && deposit != null ? price - deposit : null
  const balanceDisagrees =
    balance != null && impliedBalance != null && Math.abs(balance - impliedBalance) > 0.5

  const payout = numOrNull(form.ppsr_finance_amount)
  const payoutExceedsPrice = payout != null && price != null && payout > price

  const useImplied = () => {
    if (impliedBalance != null) set('balance_payable', String(impliedBalance))
  }

  const handleSave = async () => {
    setError(null)

    if (form.seller_postcode.trim() && !/^\d{4}$/.test(form.seller_postcode.trim())) {
      setError('An Australian postcode is four digits.')
      return
    }
    if (form.seller_bsb.trim() && !/^\d{3}-?\d{3}$/.test(form.seller_bsb.trim())) {
      setError('A BSB is six digits, written 123-456 or 123456.')
      return
    }

    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        vehicle_color: textOrNull(form.vehicle_color),
        vehicle_body: textOrNull(form.vehicle_body),
        engine_number: textOrNull(form.engine_number),
        registration_state: textOrNull(form.registration_state),
        written_off_status: textOrNull(form.written_off_status),

        seller_legal_name: textOrNull(form.seller_legal_name),
        seller_lmct: textOrNull(form.seller_lmct),
        seller_suburb: textOrNull(form.seller_suburb),
        seller_state: textOrNull(form.seller_state),
        seller_postcode: textOrNull(form.seller_postcode),

        seller_bank_name: textOrNull(form.seller_bank_name),
        seller_bsb: textOrNull(form.seller_bsb),
        seller_account_number: textOrNull(form.seller_account_number),
        seller_bank_account_name: textOrNull(form.seller_bank_account_name),

        deposit_amount: numOrNull(form.deposit_amount),
        balance_payable: numOrNull(form.balance_payable),
        expected_settlement_date: textOrNull(form.expected_settlement_date),
        lct_status: textOrNull(form.lct_status),

        financier_name: textOrNull(form.financier_name),
        ppsr_finance_amount: numOrNull(form.ppsr_finance_amount),
        payout_reference: textOrNull(form.payout_reference),
        payout_letter_date: textOrNull(form.payout_letter_date),

        inclusions: inclusionsAsked ? inclusions : null,
        known_defects: textOrNull(form.known_defects),

        updated_at: new Date().toISOString(),
      }

      const { error: err } = await supabase.from('deals').update(payload).eq('id', deal.id)
      if (err) throw err

      /* Which fields changed, not what they changed to: this log is readable by anyone who
         can read the deal, and the bank details are the whole reason to be careful here. */
      const changed = Object.keys(payload).filter((k) => {
        if (k === 'updated_at') return false
        const before = (deal as unknown as Record<string, unknown>)[k]
        const after = payload[k]
        if (k === 'inclusions') return JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)
        return (before ?? null) !== (after ?? null)
      })

      await logAction({
        dealId: deal.id,
        action: ACTIONS.DEAL_EDITED,
        actorId: user.id,
        success: true,
        details: { panel: 'purchase_agreement', fields_changed: changed },
      })

      toast.saved()
      onSaved(
        changed.length
          ? 'Purchase Agreement details saved (' + changed.length + ' field' + (changed.length === 1 ? '' : 's') + ' changed).'
          : 'Purchase Agreement details saved.'
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save those details.')
    } finally {
      setSaving(false)
    }
  }

  const text = (k: keyof typeof form, label: string, hint?: string, type = 'text') => (
    <label className="pa-field">
      <span>{label}</span>
      <input
        type={type}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        disabled={saving}
      />
      {hint ? <small>{hint}</small> : null}
    </label>
  )

  const choose = (k: keyof typeof form, label: string, options: Array<[string, string]>, hint?: string) => (
    <label className="pa-field">
      <span>{label}</span>
      <select value={form[k]} onChange={(e) => set(k, e.target.value)} disabled={saving}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      {hint ? <small>{hint}</small> : null}
    </label>
  )

  return (
    <div className="pa-panel">
      <div className="pa-head">
        <h3>Purchase Agreement details</h3>
        <p>
          Everything the Vehicle Purchase Agreement prints. Anything left blank prints as
          &ldquo;Not recorded&rdquo; rather than as a guess.
        </p>
      </div>

      {error ? <div className="pa-error">{error}</div> : null}

      <h4 className="pa-section">Vehicle</h4>
      <div className="pa-grid">
        {text('vehicle_color', 'Colour')}
        {text('vehicle_body', 'Body type', 'Sedan, wagon, ute, hatch')}
        {text('engine_number', 'Engine number')}
        {choose('registration_state', 'State of registration', STATES.map((s) => [s, s || 'Not recorded'] as [string, string]))}
        {choose('written_off_status', 'Written-off status', WRITTEN_OFF)}
      </div>

      <h4 className="pa-section">Seller</h4>
      <div className="pa-grid">
        {text('seller_legal_name', 'Full legal name / company name', 'As it should appear on the agreement')}
        {text('seller_lmct', 'LMCT (licensed dealers only)', 'Leave blank for a private seller')}
        {text('seller_suburb', 'Suburb')}
        {choose('seller_state', 'State', STATES.map((s) => [s, s || 'Not recorded'] as [string, string]))}
        {text('seller_postcode', 'Postcode')}
      </div>

      <h4 className="pa-section">Seller bank details</h4>
      <div className="pa-alert">
        These are readable by everyone who can open this deal. Row-level security cannot
        narrow three columns on a table people can already read.
      </div>
      <div className="pa-grid">
        {text('seller_bank_name', 'Bank name')}
        {text('seller_bsb', 'BSB', 'Six digits')}
        {text('seller_account_number', 'Account number')}
        {text('seller_bank_account_name', 'Account name')}
      </div>

      <h4 className="pa-section">Price and settlement</h4>
      <div className="pa-grid">
        {text('deposit_amount', 'Deposit amount (AUD)', undefined, 'number')}
        {text(
          'balance_payable',
          'Balance payable (AUD)',
          impliedBalance != null ? 'Price less deposit is ' + impliedBalance : 'Stored as agreed, not recalculated later',
          'number'
        )}
        {text('expected_settlement_date', 'Expected settlement date', undefined, 'date')}
        {choose('lct_status', 'Luxury Car Tax', LCT)}
      </div>
      {balanceDisagrees ? (
        <div className="pa-warn">
          The balance you have entered is not the agreed price less the deposit
          ({impliedBalance}). That is allowed - it is the figure both parties sign - but
          check it is deliberate.{' '}
          <button type="button" className="pa-link" onClick={useImplied} disabled={saving}>
            Use {impliedBalance}
          </button>
        </div>
      ) : null}

      <h4 className="pa-section">Encumbrance and payout</h4>
      <div className="pa-grid">
        {text('financier_name', 'Financier / secured party', "The SELLER's financier, being paid out")}
        {text('ppsr_finance_amount', 'Payout amount (AUD)', undefined, 'number')}
        {text('payout_reference', 'Payout reference / account')}
        {text('payout_letter_date', 'Payout letter date', undefined, 'date')}
      </div>
      {payoutExceedsPrice ? (
        <div className="pa-warn">
          The payout is more than the purchase price. Under clause 5 the seller has to pay
          the shortfall before settlement can proceed.
        </div>
      ) : null}

      <h4 className="pa-section">Condition and inclusions</h4>
      <div className="pa-checks">
        {INCLUSIONS.map(([key, label]) => (
          <label key={key} className="pa-check">
            <input
              type="checkbox"
              checked={inclusions.indexOf(key) !== -1}
              onChange={() => toggle(key)}
              disabled={saving}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
      <p className="pa-hint">
        {inclusionsAsked
          ? 'Recorded. Nothing ticked prints as \"asked, none supplied\".'
          : 'Not recorded yet. Tick anything supplied, or tick and untick one to record that none were.'}
      </p>

      <label className="pa-field pa-wide">
        <span>Known defects / damage</span>
        <textarea
          rows={4}
          value={form.known_defects}
          onChange={(e) => set('known_defects', e.target.value)}
          disabled={saving}
          placeholder="Describe, or write NIL"
        />
      </label>

      <div className="pa-actions">
        <button type="button" className="pa-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save agreement details'}
        </button>
      </div>
    </div>
  )
}
